import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, FileSpreadsheet, FileText, Calendar, Loader2, Check, CalendarRange } from "lucide-react";

function todayIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}
function yesterdayIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
function thisMonthIso() { return todayIso().slice(0, 7); }
function lastMonthIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
}

async function downloadBlob(url, params, filenameFallback) {
  const r = await api.get(url, { params, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const match = /filename="?([^";]+)"?/.exec(cd);
  const filename = match ? match[1] : filenameFallback;
  const blob = new Blob([r.data]);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export default function DailyReportCard({ module }) {
  const [period, setPeriod] = useState("daily"); // "daily" | "monthly"
  const [date, setDate] = useState(todayIso());
  const [month, setMonth] = useState(thisMonthIso());
  const [client, setClient] = useState("Hapag-Lloyd");
  const [shippingLines, setShippingLines] = useState(["Hapag-Lloyd", "Arkas"]);
  const [shippingLine, setShippingLine] = useState("ALL");
  const [busy, setBusy] = useState(null);
  const [justDone, setJustDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (module === "trucking") {
      api.get("/settings/shipping-lines")
        .then((r) => setShippingLines(r.data.values || []))
        .catch((e) => console.warn("shipping-lines fetch failed:", e?.message));
    }
  }, [module]);

  const fire = async (format) => {
    setBusy(format);
    setError(null);
    try {
      const isMonthly = period === "monthly";
      if (module === "inspection") {
        const path = isMonthly ? "/reports/inspection/monthly" : "/reports/inspection/daily";
        const params = isMonthly ? { month, format, client } : { date, format, client };
        const fallback = `inspection-${client.replace(/\s/g, "_")}-${isMonthly ? month : date}.${format}`;
        await downloadBlob(path, params, fallback);
      } else {
        const path = isMonthly ? "/reports/trucking/monthly" : "/reports/trucking/daily";
        const params = isMonthly
          ? { month, format, shipping_line: shippingLine }
          : { date, format, shipping_line: shippingLine };
        const label = shippingLine === "ALL" ? "All_lines" : shippingLine.replace(/\s/g, "_");
        const fallback = `trucking-${label}-${isMonthly ? month : date}.${format}`;
        await downloadBlob(path, params, fallback);
      }
      setJustDone(format);
      setTimeout(() => setJustDone(null), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  const isMonthly = period === "monthly";

  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5 sm:p-6"
      data-testid={`daily-report-card-${module}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Download size={12} /> Client reports
          </div>
          <h3 className="font-display font-extrabold text-lg text-[#0a2952]">
            {module === "inspection" ? "Inspection · per client" : "Trucking · per shipping line"}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Daily or monthly — pick a period, pick a client, download Excel or PDF.
          </p>
        </div>
        <div
          className="inline-flex items-center bg-slate-100 rounded-md p-1 self-start"
          role="tablist"
          data-testid={`report-${module}-period-toggle`}
        >
          <button
            type="button"
            data-testid={`report-${module}-period-daily`}
            onClick={() => setPeriod("daily")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] rounded transition-colors ${
              !isMonthly ? "bg-white text-[#0a2952] shadow-sm" : "text-slate-500 hover:text-[#0a2952]"
            }`}
          >
            <Calendar size={12} /> Daily
          </button>
          <button
            type="button"
            data-testid={`report-${module}-period-monthly`}
            onClick={() => setPeriod("monthly")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.1em] rounded transition-colors ${
              isMonthly ? "bg-white text-[#0a2952] shadow-sm" : "text-slate-500 hover:text-[#0a2952]"
            }`}
          >
            <CalendarRange size={12} /> Monthly
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {isMonthly ? (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 flex items-center gap-1">
              <CalendarRange size={11} /> Month
            </span>
            <input
              type="month"
              value={month}
              data-testid={`report-${module}-month`}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
            />
            <div className="flex gap-1.5 mt-1.5">
              <button
                type="button"
                data-testid={`report-${module}-this-month-btn`}
                onClick={() => setMonth(thisMonthIso())}
                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                  month === thisMonthIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                This month
              </button>
              <button
                type="button"
                data-testid={`report-${module}-last-month-btn`}
                onClick={() => setMonth(lastMonthIso())}
                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                  month === lastMonthIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Last month
              </button>
            </div>
          </label>
        ) : (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 flex items-center gap-1">
              <Calendar size={11} /> Date
            </span>
            <input
              type="date"
              value={date}
              data-testid={`report-${module}-date`}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
            />
            <div className="flex gap-1.5 mt-1.5">
              <button
                type="button"
                data-testid={`report-${module}-today-btn`}
                onClick={() => setDate(todayIso())}
                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                  date === todayIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Today
              </button>
              <button
                type="button"
                data-testid={`report-${module}-yesterday-btn`}
                onClick={() => setDate(yesterdayIso())}
                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                  date === yesterdayIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Yesterday
              </button>
            </div>
          </label>
        )}

        {module === "inspection" ? (
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">
              Client
            </span>
            <input
              type="text"
              value={client}
              data-testid="report-inspection-client"
              onChange={(e) => setClient(e.target.value)}
              placeholder="e.g. Hapag-Lloyd"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none"
            />
            <span className="text-[10px] text-slate-400 mt-1 block">
              Used only for the report header — does not filter records (all MPS direct gate-ins for the period are included).
            </span>
          </label>
        ) : (
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">
              Shipping line
            </span>
            <select
              value={shippingLine}
              data-testid="report-trucking-line"
              onChange={(e) => setShippingLine(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none bg-white"
            >
              <option value="ALL">All shipping lines</option>
              {shippingLines.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Filters trucking trips by shipping line for the chosen period.
            </span>
          </label>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          data-testid={`report-${module}-xlsx-btn`}
          onClick={() => fire("xlsx")}
          disabled={busy !== null}
          className="inline-flex items-center justify-center gap-2 bg-[#0a2952] hover:bg-[#071f40] disabled:opacity-60 disabled:cursor-not-allowed text-white py-3 rounded-md font-semibold text-sm transition-all hover:-translate-y-[1px] hover:shadow"
        >
          {busy === "xlsx" ? <Loader2 size={16} className="animate-spin" /> : justDone === "xlsx" ? <Check size={16} /> : <FileSpreadsheet size={16} />}
          {busy === "xlsx" ? "Building Excel…" : justDone === "xlsx" ? "Downloaded" : `Excel · ${isMonthly ? "Monthly" : "Daily"}`}
        </button>
        <button
          type="button"
          data-testid={`report-${module}-pdf-btn`}
          onClick={() => fire("pdf")}
          disabled={busy !== null}
          className="inline-flex items-center justify-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-60 disabled:cursor-not-allowed text-white py-3 rounded-md font-semibold text-sm transition-all hover:-translate-y-[1px] hover:shadow"
        >
          {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : justDone === "pdf" ? <Check size={16} /> : <FileText size={16} />}
          {busy === "pdf" ? "Building PDF…" : justDone === "pdf" ? "Downloaded" : `PDF · ${isMonthly ? "Monthly" : "Daily"}`}
        </button>
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2"
          data-testid={`report-${module}-error`}
        >
          {error}
        </div>
      )}
    </div>
  );
}
